import { useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { ViewMode } from '../models/preferences';
import {
  startAndEndOfWorkMonth,
  startAndEndOfWorkWeek,
} from '../services/dateService';
import { Holiday } from '../models/holidays';
import { useGetDaysOffQuery } from '../store/daysOff/daysOffService';
import {
   formatDateOnly,
} from '../services/dateService';

const monthMode = (daysOff: Array<Holiday>, targetDate: Date) => {
  const { startDay, endDay } = startAndEndOfWorkMonth(targetDate);
  const matchedDates = daysOff?.filter(
    (el) =>
      new Date(formatDateOnly(el.dayOffDate)) >= startDay && new Date(formatDateOnly(el.dayOffDate)) <= endDay
  );

  return matchedDates;
};

const weekMode = (
  daysOff: Array<Holiday>,
  targetDate: Date,
  isWorkWeek: boolean | undefined
) => {
  const { startWeek, endWeek } = startAndEndOfWorkWeek(
    Boolean(isWorkWeek),
    targetDate
  );
  const matchedDates = daysOff?.filter(
    (el) =>
      new Date(formatDateOnly(el.dayOffDate)) >= startWeek && new Date(formatDateOnly(el.dayOffDate)) <= endWeek
  );

  return matchedDates;
};

const dayMode = (daysOff: Array<Holiday>, targetDate: Date) => {
  const day = daysOff?.find(
    (el) => format(targetDate, 'P') === format(new Date(formatDateOnly(el.dayOffDate)), 'P')
  );

  if (day) return [day];

  return [];
};

interface UseDayOffProp {
  targetDate: Date;
  country: string | null;
  mode?: ViewMode;
  isWorkWeek?: boolean;
}

interface UseDaysOff {
  targetHolidays: Holiday[];
  isLoadingDaysOff: boolean;
  dayOffError?: unknown | null;
  getTargetHolidays: (date: Date) => Holiday[];
}

const useDaysOff = ({
  targetDate,
  country,
  mode,
  isWorkWeek,
}: UseDayOffProp): UseDaysOff => {
  const targetYear = useMemo(
    () => Number(format(targetDate, 'yyyy')),
    [targetDate]
  );

  const {
    data,
    isLoading,
    isFetching,
    error: dayOffError,
  } = useGetDaysOffQuery({
    filterDate: targetYear,
    country: country || '',
  });

  const daysOff = useMemo(
    () =>
      data
        ? [...data].sort((a, b) => (a.dayOffDate > b.dayOffDate ? 1 : -1))
        : [],
    [data]
  );

  const isLoadingDaysOff = isLoading || isFetching;

  const getTargetHolidays = useCallback(
    (date: Date) => {
      switch (mode) {
        case ViewMode.MONTH:
          return monthMode(daysOff, date);
        case ViewMode.WEEK:
        case ViewMode.WORK_WEEK:
          return weekMode(daysOff, date, isWorkWeek);
        case ViewMode.DAY:
          return dayMode(daysOff, date);

        default:
          return daysOff;
      }
    },
    [daysOff, isWorkWeek, mode]
  );

  const targetHolidays = useMemo(
    () => getTargetHolidays(targetDate),
    [getTargetHolidays, targetDate]
  );

  return { targetHolidays, getTargetHolidays, isLoadingDaysOff, dayOffError };
};

export default useDaysOff;
